/**
 * 兄弟対応: 選択中の子（learner）を管理。
 * 1人のときは自動でその子を選択。2人以上のときはヘッダーで切り替え、選択を localStorage に保持。
 */

import { ref, computed } from 'vue'
import { supabase } from '../supabaseClient'
import { getAuthUserShared } from './useAuthUser.js'
import { createKeyedSingleFlight } from '../utils/keyedSingleFlight.js'
import { classifyGetUserResult } from '../utils/guardAccess.js'

const STORAGE_KEY_PREFIX = 'otoeigo_selected_learner_'

const learners = ref([])
const selectedLearnerId = ref(null)
// 直近の学習者一覧の取得が失敗したか（0件と区別する。指示書 A-10）
const learnersLoadError = ref(false)
let currentUserId = null
// 学習者一覧の取得: 同じユーザーの実行中の取得は共有し、古い応答は捨てる
const learnersSingleFlight = createKeyedSingleFlight()
let loadSeq = 0

/** 親の user_id で保存していた選択 learner_id を取得 */
function getStoredLearnerId(userId) {
  if (!userId) return null
  try {
    return localStorage.getItem(STORAGE_KEY_PREFIX + userId) || null
  } catch {
    return null
  }
}

/** 選択 learner_id を保存 */
function setStoredLearnerId(userId, learnerId) {
  if (!userId) return
  try {
    if (learnerId) {
      localStorage.setItem(STORAGE_KEY_PREFIX + userId, learnerId)
    } else {
      localStorage.removeItem(STORAGE_KEY_PREFIX + userId)
    }
  } catch (_) {}
}

/**
 * 学習者一覧を読み込み、選択を復元または初期化する。
 * 認証済みでない場合は何もしない。
 * @returns {Promise<'ok'|'empty'|'error'|'no_user'>}
 *   取得エラーは 'error'。そのときは前回までの一覧・選択を消さない（0件＝未登録と誤判定しないため。指示書 A-10）
 */
export async function loadLearners() {
  // 認証の確認は、ガード・Top と共有する（指示書 A3）
  let userResult
  try {
    userResult = await getAuthUserShared()
  } catch (e) {
    userResult = { data: { user: null }, error: e || new Error('get_user_failed') }
  }
  const userKind = classifyGetUserResult(userResult)
  const user = userKind === 'user' ? userResult.data.user : null
  if (!user?.id) {
    // 確認の通信に失敗しただけなら、未ログインと扱わない（前回までの一覧・選択を残す）
    if (userKind === 'error') {
      learnersLoadError.value = true
      return 'error'
    }
    loadSeq += 1
    learnersSingleFlight.clear()
    learners.value = []
    selectedLearnerId.value = null
    currentUserId = null
    learnersLoadError.value = false
    return 'no_user'
  }
  // 同じユーザーの一覧の取得が実行中なら、それを待つ（共通レイアウトと Top が同時に呼ぶため。指示書 ⑨・A-15）
  return learnersSingleFlight.run(user.id, () => fetchLearnersFor(user.id))
}

async function fetchLearnersFor(userId) {
  const seq = ++loadSeq
  const previousUserId = currentUserId
  currentUserId = userId

  const { data, error } = await supabase
    .from('learners')
    .select('*')
    .eq('parent_id', userId)
    .eq('type', 'child')
    .order('created_at', { ascending: true })

  // 取得中に別のユーザーへの読み込み・ログアウトがあった。古い応答で一覧・選択を書き換えない（指示書 A-16）。
  // 0件（未登録）とは扱わない
  if (seq !== loadSeq) return 'error'

  if (error) {
    console.error('学習者一覧取得エラー:', error)
    // 前回までの一覧・選択は残す（別ユーザーの値なら残さない）
    if (previousUserId !== userId) {
      learners.value = []
      selectedLearnerId.value = null
    }
    learnersLoadError.value = true
    return 'error'
  }
  learnersLoadError.value = false

  learners.value = data || []

  if (learners.value.length === 0) {
    selectedLearnerId.value = null
    setStoredLearnerId(userId, null)
    return 'empty'
  }

  if (learners.value.length === 1) {
    selectedLearnerId.value = learners.value[0].id
    setStoredLearnerId(userId, learners.value[0].id)
    return 'ok'
  }

  const stored = getStoredLearnerId(userId)
  const found = stored && learners.value.some(l => l.id === stored)
  selectedLearnerId.value = found ? stored : learners.value[0].id
  if (!found) {
    setStoredLearnerId(userId, learners.value[0].id)
  }
  return 'ok'
}

/**
 * 実行中の学習者一覧の取得の共有をやめる（Top の読み込みが期限を過ぎたとき）。
 * 次の loadLearners は新しく取得する。止まった取得の応答が後から届いても、新しい取得があれば一覧を書き換えない
 */
export function abandonLearnersInFlight() {
  learnersSingleFlight.clear()
}

/** 選択中の子を切り替える（兄弟が2人以上のとき用） */
export function setSelectedLearner(learnerId) {
  if (learnerId && learners.value.some(l => l.id === learnerId)) {
    selectedLearnerId.value = learnerId
    if (currentUserId) setStoredLearnerId(currentUserId, learnerId)
  }
}

export function useSelectedLearner() {
  const selectedLearner = computed(() =>
    learners.value.find(l => l.id === selectedLearnerId.value) || null
  )
  const hasMultipleLearners = computed(() => learners.value.length > 1)

  return {
    learners,
    selectedLearnerId,
    selectedLearner,
    hasMultipleLearners,
    learnersLoadError,
    loadLearners,
    setSelectedLearner
  }
}
